import { apiFetch } from '@/lib/api';

export interface BillingPriceDto {
  id: string;
  interval: 'month' | 'year';
  currency: string;
  unitAmount: number;
  trialDays: number;
  stripePriceId: string | null;
  paymentLinkUrl: string | null;
  isActive: boolean;
}

export interface BillingPlanFeatureDto {
  id: string;
  text: string;
  isIncluded: boolean;
  category: string | null;
  displayOrder: number;
  tooltipHtml: string | null;
}

export interface BillingPlanDto {
  id: string;
  key: string;
  name: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  stripeProductId: string | null;
  includedCredits: number;
  prices: BillingPriceDto[];
  limits: Array<{ id: string; key: string; value: number; isHardLimit: boolean }>;
  flags: Record<string, unknown>;
  flagDetails: Array<{ id: string; key: string; value: unknown }>;
  features: BillingPlanFeatureDto[];
}

export interface UsageMetricDto {
  key: string;
  used: number;
  limit?: number | null;
  isHardLimit?: boolean;
}

export interface UsageSummaryDto {
  periodStart: string | null;
  periodEnd: string | null;
  metrics: UsageMetricDto[];
}

export interface BillingTransactionDto {
  id: string;
  organizationId: string;
  amount: number;
  currency: string;
  status: 'succeeded' | 'failed' | 'refunded' | 'pending';
  paidAt: string | null;
  nextPaymentAttemptAt: string | null;
  createdAt: string;
  stripeInvoiceId: string | null;
  stripePaymentIntentId: string | null;
  stripeChargeId: string | null;
  metadata: Record<string, unknown> | null;
  planKey: string | null;
  planName: string | null;
  priceInterval: 'month' | 'year' | null;
  priceUnitAmount: number | null;
}

export interface BillingAlertDto {
  type: 'payment_failed' | 'past_due' | 'upcoming_payment';
  message: string;
  occurredAt: string | null;
  referenceId?: string | null;
}

export interface BillingTransactionListDto {
  items: BillingTransactionDto[];
  total: number;
}

export interface SubscriptionSnapshotDto {
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  plan: BillingPlanDto | null;
  price: BillingPriceDto | null;
  usage: UsageSummaryDto | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  alerts: BillingAlertDto[];
  recentTransactions: BillingTransactionDto[];
}

export class BillingService {
  private readonly baseUrl: string;

  constructor() {
    const vURL = (import.meta as any).env?.VITE_API_URL as string | undefined;
    this.baseUrl = vURL ? vURL.replace(/\/$/, '') : '';
  }

  async listPlans(): Promise<BillingPlanDto[]> {
    const res = await apiFetch(`${this.baseUrl}/api/billing/plans`);
    if (!res.ok) throw new Error('Failed to load billing plans');
    const payload = await res.json();
    return payload?.data ?? [];
  }

  /** Public plans endpoint — no auth required, safe for landing page */
  static async listPublicPlans(): Promise<BillingPlanDto[]> {
    const vURL = (import.meta as any).env?.VITE_API_URL as string | undefined;
    const base = vURL ? vURL.replace(/\/$/, '') : '';
    const res = await fetch(`${base}/api/public/plans`);
    if (!res.ok) throw new Error('Failed to load plans');
    const payload = await res.json();
    return payload?.data ?? [];
  }

  async createCheckoutSession(body: { planKey: string; interval: 'month' | 'year'; source?: 'onboarding' | 'billing' | 'paywall' }): Promise<{ id: string; url: string }> {
    const res = await apiFetch(`${this.baseUrl}/api/billing/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const payload = await safeJson(res);
      throw new Error(payload?.error?.message || 'Failed to start checkout');
    }
    const payload = await res.json();
    return payload?.data;
  }

  async endTrialNow(): Promise<void> {
    const res = await apiFetch(`${this.baseUrl}/api/billing/end-trial-now`, { method: 'POST' });
    if (!res.ok) {
      const payload = await safeJson(res);
      throw new Error(payload?.error?.message || 'Failed to start plan');
    }
  }

  async createPortalSession(returnUrl?: string, source?: 'onboarding' | 'billing'): Promise<{ url: string }> {
    const res = await apiFetch(`${this.baseUrl}/api/billing/create-portal-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnUrl, source }),
    });
    if (!res.ok) {
      const payload = await safeJson(res);
      throw new Error(payload?.error?.message || 'Failed to open billing portal');
    }
    const payload = await res.json();
    return payload?.data;
  }

  async getSubscription(): Promise<SubscriptionSnapshotDto | null> {
    const res = await apiFetch(`${this.baseUrl}/api/billing/subscription`);
    if (!res.ok) {
      const payload = await safeJson(res);
      throw new Error(payload?.error?.message || 'Failed to load subscription');
    }
    const payload = await res.json();
    return (payload?.data ?? null) as SubscriptionSnapshotDto | null;
  }

  async getUsage(): Promise<UsageSummaryDto | null> {
    const res = await apiFetch(`${this.baseUrl}/api/billing/usage`);
    if (!res.ok) {
      const payload = await safeJson(res);
      throw new Error(payload?.error?.message || 'Failed to load usage');
    }
    const payload = await res.json();
    return (payload?.data ?? null) as UsageSummaryDto | null;
  }

  async listTransactions(params: { status?: BillingTransactionDto['status']; limit?: number; offset?: number } = {}): Promise<BillingTransactionListDto> {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const qs = query.toString();
    const res = await apiFetch(`${this.baseUrl}/api/billing/transactions${qs ? `?${qs}` : ''}`);
    if (!res.ok) {
      const payload = await safeJson(res);
      throw new Error(payload?.error?.message || 'Failed to load transactions');
    }
    const payload = await res.json();
    return payload?.data as BillingTransactionListDto;
  }
}

async function safeJson(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}
